import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { FadeIn } from "@/components/FadeIn";
import { RevealHeading } from "@/components/RevealHeading";
import { SectionLabel } from "@/components/SectionLabel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCalendly } from "@/components/CalendlyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industryOptions = ["Restaurant / Hospitality", "Medical / Healthcare", "Legal", "Fitness / Wellness", "Professional Services", "Local Business", "Entrepreneur / Personal Brand", "Other"];
const revenueOptions = ["Pre-Revenue", "$0 – $10K/mo", "$10K – $50K/mo", "$50K – $100K/mo", "$100K+/mo"];
const budgetOptions = ["Under $1,000/mo", "$1,000 – $3,000/mo", "$3,000 – $7,500/mo", "$7,500+/mo", "Not Sure Yet"];
const hearOptions = ["Google Search", "Instagram", "LinkedIn", "Referral", "Other"];
const serviceOptions = ["Brand Strategy", "Digital Marketing", "Social Media & Content", "Web Design & Development", "Lead Generation", "Automation & AI", "Local Business Growth", "Consulting & Growth Strategy"];

const initialState = {
  first_name: "", last_name: "", company: "", email: "", phone: "", website: "",
  industry: "", revenue_range: "", challenge: "", services_interested: [],
  budget: "", hear_about_us: "", message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { openCalendly } = useCalendly();

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleService = (service) => {
    setForm((f) => ({
      ...f,
      services_interested: f.services_interested.includes(service)
        ? f.services_interested.filter((s) => s !== service)
        : [...f.services_interested, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.company || !form.email || !form.phone || !form.industry || !form.revenue_range || !form.challenge || !form.budget) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      navigate("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact"
        description="Tell DULORME LABS where your business is today and where you want to take it. Request a strategy call."
        path="/contact"
      />

      <section className="pt-40 pb-16 border-b border-white/10">
        <div className="container-custom">
          <SectionLabel>Contact</SectionLabel>
          <RevealHeading
            tag="h1"
            lines={["LET'S BUILD", "WHAT'S NEXT."]}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight text-white max-w-3xl"
          />
          <FadeIn delay={0.3}>
            <p className="mt-8 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Tell us where your business is today and where you want to take it.
            </p>
            <button
              data-testid="contact-page-book-call-btn"
              onClick={openCalendly}
              className="mt-8 bg-white text-black font-bold text-xs tracking-widest uppercase px-8 py-4 hover:bg-violet-500 hover:text-white transition-colors duration-300"
            >
              Book A Strategy Call Directly
            </button>
          </FadeIn>
        </div>
      </section>

      <section className="py-24">
        <div className="container-custom max-w-3xl">
          <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-10">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="First Name" required>
                <Input data-testid="contact-input-first-name" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Last Name" required>
                <Input data-testid="contact-input-last-name" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Company" required>
                <Input data-testid="contact-input-company" value={form.company} onChange={(e) => update("company", e.target.value)} className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Email" required>
                <Input data-testid="contact-input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Phone" required>
                <Input data-testid="contact-input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Website">
                <Input data-testid="contact-input-website" value={form.website} onChange={(e) => update("website", e.target.value)} className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Industry" required>
                <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                  <SelectTrigger data-testid="contact-select-industry" className="bg-white/5 border-white/10 h-12">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>{industryOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Current Monthly Revenue" required>
                <Select value={form.revenue_range} onValueChange={(v) => update("revenue_range", v)}>
                  <SelectTrigger data-testid="contact-select-revenue" className="bg-white/5 border-white/10 h-12">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>{revenueOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Primary Business Challenge" required>
              <Textarea data-testid="contact-input-challenge" value={form.challenge} onChange={(e) => update("challenge", e.target.value)} className="bg-white/5 border-white/10 min-h-[90px]" />
            </Field>

            <Field label="Services Interested In">
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {serviceOptions.map((service) => (
                  <button
                    type="button"
                    key={service}
                    data-testid={`contact-service-toggle-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleService(service)}
                    className={`text-left text-sm px-4 py-3 border transition-colors duration-300 ${
                      form.services_interested.includes(service)
                        ? "border-violet-400 bg-violet-500/10 text-white"
                        : "border-white/10 text-zinc-500 hover:border-white/30"
                    }`}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Estimated Marketing Budget" required>
                <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                  <SelectTrigger data-testid="contact-select-budget" className="bg-white/5 border-white/10 h-12">
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent>{budgetOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="How Did You Hear About Us?">
                <Select value={form.hear_about_us} onValueChange={(v) => update("hear_about_us", v)}>
                  <SelectTrigger data-testid="contact-select-hear-about" className="bg-white/5 border-white/10 h-12">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>{hearOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Message">
              <Textarea data-testid="contact-input-message" value={form.message} onChange={(e) => update("message", e.target.value)} className="bg-white/5 border-white/10 min-h-[120px]" />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="w-full sm:w-auto bg-white text-black font-bold text-xs tracking-widest uppercase px-10 py-5 hover:bg-violet-500 hover:text-white transition-colors duration-300 disabled:opacity-50"
            >
              {submitting ? "Sending..." : "Request A Strategy Call"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-500 mb-3 block">
        {label} {required && <span className="text-violet-400">*</span>}
      </label>
      {children}
    </div>
  );
}
